import type { PreorderCartItem } from '@pos/shared';

export interface PreorderCartSummaryProps {
  cart: PreorderCartItem[];
  totalAmount: number;
  finalAmount: number;
  /** 折扣後小計（元），用於顯示小計與稅金 breakdown */
  subtotalAfterDiscount: number;
  /** 稅金（元） */
  taxAmount: number;
  /** 稅率（basis points），例如 500 = 5%，用於顯示「稅金 (5%)」 */
  taxRateBps?: number;
  pointsRedeemAmount: number;
  onCheckout: () => void;
  onUpdateQuantity: (
    productId: string,
    newQuantity: number,
    productVariantId?: string
  ) => void;
  onRemoveFromCart: (productId: string, productVariantId?: string) => void;
  formatMoney: (yuan: number) => string;
  formatTwdFromCents: (cents: number) => string;
}

/**
 * 購物車摘要與「送出預購」按鈕區塊
 */
export function PreorderCartSummary({
  cart,
  totalAmount,
  finalAmount,
  subtotalAfterDiscount,
  taxAmount,
  taxRateBps = 500,
  pointsRedeemAmount,
  onCheckout,
  onUpdateQuantity,
  onRemoveFromCart,
  formatMoney,
  formatTwdFromCents,
}: PreorderCartSummaryProps) {
  if (cart.length === 0) return null;

  const taxRatePercent = taxRateBps / 100;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-20">
      <div className="px-4 py-3 max-h-64 overflow-y-auto">
        <h3 className="text-[0.875rem] font-medium text-gray-900 mb-2">
          購物車 ({cart.length} 項)
        </h3>
        <div className="space-y-2">
          {cart.map((item, idx) => (
            <div
              key={`${item.productId}-${item.productVariantId ?? ''}-${idx}`}
              className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.productName}
                  {item.variantLabel ? (
                    <span className="text-gray-600 font-normal">
                      {' '}
                      · {item.variantLabel}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-gray-600">
                  {formatTwdFromCents(item.productPriceTwd)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onUpdateQuantity(
                      item.productId,
                      item.quantity - 1,
                      item.productVariantId
                    );
                  }}
                  className="w-7 h-7 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm"
                >
                  −
                </button>
                <span className="w-8 text-center text-sm font-medium">
                  {Number.isFinite(Number(item.quantity))
                    ? Number(item.quantity)
                    : Number(item.quantity) || 0}
                </span>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onUpdateQuantity(
                      item.productId,
                      item.quantity + 1,
                      item.productVariantId
                    );
                  }}
                  className="w-7 h-7 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm"
                  disabled={item.quantity >= item.remainingQuantity}
                >
                  +
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    onRemoveFromCart(item.productId, item.productVariantId);
                  }}
                  className="w-6 h-6 text-red-500 hover:text-red-700"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
        {/* 小計、稅金 breakdown（面交購物車顯示稅金） */}
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-[0.8125rem] text-gray-600">
          {pointsRedeemAmount > 0 && (
            <div className="flex justify-between">
              <span>商品總額</span>
              <span className="line-through">{formatMoney(totalAmount)}</span>
            </div>
          )}
          {pointsRedeemAmount > 0 && (
            <div className="flex justify-between">
              <span>點數折抵</span>
              <span>-{formatMoney(pointsRedeemAmount)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>小計</span>
            <span>{formatMoney(subtotalAfterDiscount)}</span>
          </div>
          <div className="flex justify-between">
            <span>稅金 ({taxRatePercent}%)</span>
            <span>{formatMoney(taxAmount)}</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-[0.875rem] font-medium text-gray-900">
              {pointsRedeemAmount > 0 ? '應付' : '應付'}
            </span>
            <span className="text-[1.125rem] font-bold text-blue-600">
              {formatMoney(finalAmount)}
            </span>
          </div>
          <button
            type="button"
            onClick={onCheckout}
            disabled={cart.length === 0}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all text-base whitespace-nowrap"
          >
            送出預購
          </button>
        </div>
      </div>
    </div>
  );
}
